import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Card,
  CardContent,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Verified as VerifiedIcon,
  Security as SecurityIcon,
  AccountTree as FamilyTreeIcon,
  LinkOff as UnlinkIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { adminService } from '../../services/adminService';
import { auditLogService } from '../../services/auditLogService';
import { PersonLinkService } from '../../services/personLinkService';
import { UserDetailAdmin, AuditLog } from '../../types/admin';
import { useUser } from '../../contexts/AuthContext';

/**
 * UserDetail Component
 * Detailed view of a single user with edit capabilities
 *
 * Features:
 * - Complete user information display
 * - Edit role
 * - Approve/reject/suspend/delete user
 * - View user activity history
 * - View audit log for actions on this user
 * - User statistics
 */
const UserDetail: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const currentUser = useUser();

  // State
  const [user, setUser] = useState<UserDetailAdmin | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [editRoleDialog, setEditRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState<string>('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  /**
   * Get user status string from flags
   */
  const getUserStatus = (): string => {
    if (!user) return 'unknown';
    if (user.isSuspended) return 'suspended';
    if (!user.isApproved) return 'pending';
    return 'approved';
  };

  /**
   * Load user data on mount
   */
  useEffect(() => {
    if (userId) {
      loadUserData();
    }
  }, [userId]);

  /**
   * Load user details and audit logs
   */
  const loadUserData = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const [userData, logs] = await Promise.all([
        adminService.getUserById(userId),
        auditLogService.getTargetUserAuditLogs(userId, 50),
      ]);

      setUser(userData);
      setAuditLogs(logs);
      setNewRole(userData.role);
    } catch (err: any) {
      console.error('Error loading user data:', err);
      setError(err.message || 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle approve user
   */
  const handleApprove = async () => {
    if (!userId || !currentUser?.uid) return;

    try {
      await adminService.approveUser(userId, currentUser.uid);
      showSnackbar('User approved successfully', 'success');
      await loadUserData();
    } catch (err: any) {
      console.error('Error approving user:', err);
      showSnackbar(err.message || 'Failed to approve user', 'error');
    }
  };

  /**
   * Handle reject user
   */
  const handleReject = () => {
    setConfirmDialog({
      open: true,
      title: 'Reject User',
      message: 'Are you sure you want to reject this user registration?',
      action: async () => {
        if (!userId || !currentUser?.uid) return;

        try {
          await adminService.rejectUser(userId, currentUser.uid);
          showSnackbar('User rejected successfully', 'success');
          navigate('/admin/users');
        } catch (err: any) {
          console.error('Error rejecting user:', err);
          showSnackbar(err.message || 'Failed to reject user', 'error');
        }
      },
    });
  };

  /**
   * Handle suspend user
   */
  const handleSuspend = () => {
    setConfirmDialog({
      open: true,
      title: user?.isSuspended ? 'Unsuspend User' : 'Suspend User',
      message: user?.isSuspended
        ? 'Are you sure you want to unsuspend this user?'
        : 'Are you sure you want to suspend this user? They will lose access to the system.',
      action: async () => {
        if (!userId || !currentUser?.uid || !user) return;

        try {
          if (user.isSuspended) {
            await adminService.unsuspendUser(
              userId,
              currentUser.uid,
              currentUser.displayName || 'Unknown Admin'
            );
            showSnackbar('User unsuspended successfully', 'success');
          } else {
            await adminService.suspendUser(
              userId,
              currentUser.uid,
              currentUser.displayName || 'Unknown Admin',
              'Suspended by admin'
            );
            showSnackbar('User suspended successfully', 'success');
          }
          await loadUserData();
        } catch (err: any) {
          console.error('Error suspending/unsuspending user:', err);
          showSnackbar(err.message || 'Failed to update user suspension status', 'error');
        }
      },
    });
  };

  /**
   * Handle delete user
   */
  const handleDelete = () => {
    setConfirmDialog({
      open: true,
      title: 'Delete User',
      message:
        'Are you sure you want to delete this user? This action cannot be undone and will remove all their data.',
      action: async () => {
        if (!userId || !currentUser?.uid) return;

        try {
          await adminService.deleteUser(
            userId,
            currentUser.uid,
            currentUser.displayName || 'Unknown Admin',
            'Deleted by admin'
          );
          showSnackbar('User deleted successfully', 'success');
          navigate('/admin/users');
        } catch (err: any) {
          console.error('Error deleting user:', err);
          showSnackbar(err.message || 'Failed to delete user', 'error');
        }
      },
    });
  };

  /**
   * Handle role change
   */
  const handleRoleChange = async () => {
    if (!userId || !currentUser?.uid || !newRole || newRole === user?.role) {
      setEditRoleDialog(false);
      return;
    }

    try {
      await adminService.updateUserRole(
        userId,
        newRole as any,
        currentUser.uid,
        currentUser.displayName || 'Unknown Admin'
      );
      showSnackbar('User role updated successfully', 'success');
      setEditRoleDialog(false);
      await loadUserData();
    } catch (err: any) {
      console.error('Error updating user role:', err);
      showSnackbar(err.message || 'Failed to update user role', 'error');
    }
  };

  /**
   * Handle unlink user from person
   */
  const handleUnlink = () => {
    setConfirmDialog({
      open: true,
      title: 'Unlink User from Person',
      message:
        'Are you sure you want to unlink this user from their person profile? This will remove the connection between the user account and the family tree profile.',
      action: async () => {
        if (!userId || !currentUser?.uid) return;

        try {
          await PersonLinkService.unlinkUser(
            userId,
            currentUser.uid,
            currentUser.displayName || 'Unknown Admin',
            'Unlinked by admin'
          );
          showSnackbar('User unlinked successfully', 'success');
          await loadUserData();
        } catch (err: any) {
          console.error('Error unlinking user:', err);
          showSnackbar(err.message || 'Failed to unlink user', 'error');
        }
      },
    });
  };

  /**
   * Show snackbar message
   */
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Close snackbar
   */
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  /**
   * Close confirm dialog
   */
  const handleCloseConfirmDialog = () => {
    setConfirmDialog(null);
  };

  /**
   * Execute confirm dialog action
   */
  const handleConfirmDialogAction = () => {
    if (confirmDialog?.action) {
      confirmDialog.action();
    }
    handleCloseConfirmDialog();
  };

  /**
   * Get role badge color
   */
  const getRoleColor = (
    role: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'editor':
        return 'warning';
      case 'contributor':
        return 'info';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  /**
   * Get status color
   */
  const getStatusColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  /**
   * Get user initials
   */
  const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Get action label for audit log
   */
  const getActionLabel = (log: AuditLog): string => {
    switch (log.action) {
      case 'user_approved':
        return 'User was approved';
      case 'user_rejected':
        return 'User was rejected';
      case 'role_changed':
        return `Role changed to ${log.details.newRole}`;
      case 'user_deleted':
        return 'User was deleted';
      case 'user_suspended':
        return 'User was suspended';
      case 'user_unsuspended':
        return 'User was unsuspended';
      default:
        return log.action.replace(/_/g, ' ');
    }
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  /**
   * Render error state
   */
  if (error || !user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/users')} sx={{ mb: 2 }}>
          Back to Users
        </Button>
        <Alert severity="error">{error || 'User not found'}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/users')}
          sx={{ mb: 3 }}
        >
          Back to Users
        </Button>

        {/* User Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="flex-start" gap={3}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                bgcolor: `${getRoleColor(user.role)}.main`,
                fontSize: '2.5rem',
              }}
            >
              {getInitials(user.displayName)}
            </Avatar>

            <Box flexGrow={1}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="h4" fontWeight={700}>
                  {user.displayName}
                </Typography>
                {user.isEmailVerified && (
                  <Tooltip title="Email verified">
                    <VerifiedIcon color="primary" />
                  </Tooltip>
                )}
              </Box>

              <Typography variant="body1" color="text.secondary" gutterBottom>
                {user.email}
              </Typography>

              <Box display="flex" gap={1} mt={2}>
                <Chip
                  label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  color={getRoleColor(user.role)}
                />
                <Chip
                  label={getUserStatus().charAt(0).toUpperCase() + getUserStatus().slice(1)}
                  color={getStatusColor(getUserStatus())}
                />
                {user.isSuspended && <Chip label="Suspended" color="error" icon={<BlockIcon />} />}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box display="flex" flexDirection="column" gap={1}>
              {getUserStatus() === 'pending' && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleApprove}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleReject}
                  >
                    Reject
                  </Button>
                </>
              )}
              {getUserStatus() === 'approved' && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditRoleDialog(true)}
                  >
                    Change Role
                  </Button>
                  <Button
                    variant="outlined"
                    color={user.isSuspended ? 'success' : 'warning'}
                    startIcon={<BlockIcon />}
                    onClick={handleSuspend}
                  >
                    {user.isSuspended ? 'Unsuspend' : 'Suspend'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
            <Tab label="Details" />
            <Tab label="Activity" />
            <Tab label="Audit Log" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Account Information */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Account Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List dense>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.light' }}>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary="User ID" secondary={user.uid} />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'info.light' }}>
                          <EmailIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Email"
                        secondary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {user.email}
                            {user.isEmailVerified && (
                              <Chip label="Verified" size="small" color="success" />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.light' }}>
                          <SecurityIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary="Role" secondary={user.role} />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'success.light' }}>
                          <FamilyTreeIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Linked Person"
                        secondary={
                          user.personId ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography
                                variant="body2"
                                color="primary"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/people/${user.personId}`)}
                              >
                                View Profile
                              </Typography>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<UnlinkIcon />}
                                onClick={handleUnlink}
                              >
                                Unlink
                              </Button>
                            </Box>
                          ) : (
                            'Not linked'
                          )
                        }
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Timestamps */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Timestamps
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List dense>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'success.light' }}>
                          <CalendarIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Created At"
                        secondary={
                          user.createdAt
                            ? format(user.createdAt, 'PPpp')
                            : 'Unknown'
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'info.light' }}>
                          <HistoryIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Last Login"
                        secondary={
                          user.lastLoginAt
                            ? format(user.lastLoginAt, 'PPpp')
                            : 'Never'
                        }
                      />
                    </ListItem>
                    {user.approvedAt && (
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'success.light' }}>
                            <CheckCircleIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary="Approved At"
                          secondary={format(user.approvedAt, 'PPpp')}
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Statistics */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Statistics
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" fontWeight={700}>
                          {user.totalContributions || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total Contributions
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" fontWeight={700}>
                          {user.personsCreated || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Persons Created
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" fontWeight={700}>
                          {user.photosUploaded || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Photos Uploaded
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" fontWeight={700}>
                          {user.relationshipsCreated || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Relationships Created
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              User Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Activity history feature coming soon
            </Typography>
          </Paper>
        )}

        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600} mb={2}>
              Audit Log
            </Typography>
            {auditLogs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No audit log entries found for this user
              </Typography>
            ) : (
              <List>
                {auditLogs.map((log) => (
                  <React.Fragment key={log.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar>
                          <HistoryIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={getActionLabel(log)}
                        secondary={
                          <>
                            <Typography component="span" variant="body2">
                              By {log.userName} ({log.userEmail})
                            </Typography>
                            <br />
                            <Typography component="span" variant="caption">
                              {log.timestamp ? format(log.timestamp, 'PPpp') : 'Unknown time'}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        )}

        {/* Edit Role Dialog */}
        <Dialog open={editRoleDialog} onClose={() => setEditRoleDialog(false)}>
          <DialogTitle>Change User Role</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Select a new role for {user.displayName}
            </DialogContentText>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                label="Role"
              >
                <MenuItem value="viewer">Viewer</MenuItem>
                <MenuItem value="contributor">Contributor</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditRoleDialog(false)}>Cancel</Button>
            <Button onClick={handleRoleChange} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirm Dialog */}
        <Dialog open={confirmDialog?.open || false} onClose={handleCloseConfirmDialog}>
          <DialogTitle>{confirmDialog?.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{confirmDialog?.message}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
            <Button onClick={handleConfirmDialogAction} color="error" variant="contained">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default UserDetail;
